import { useAtom } from "jotai";
import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
} from "react";
import {
  type ChatbotMessagesByConversationIdQueryVariables,
  ModelSortDirection,
} from "~/api/API";
import { updateChatbotConversation } from "~/api/graphql/mutations";
import { chatbotConversationsByUserUpdatedAt } from "~/api/graphql/queries";
import {
  type ChatbotMessagesByConversationIdWithAttachmentsResult,
  chatbotMessagesByConversationIdWithAttachments,
} from "~/api/graphql-custom/queries";
import {
  chatbotLastConversationIdAtom,
  indyWebSearchPreferencesAtom,
} from "~/atoms/chatbot";
import { useAuthSession } from "~/auth/AuthSessionProvider";
import { getIdToken } from "~/auth/authToken";
import { useCurrentUser } from "~/auth/UserContext";
import { getConfig } from "~/config/configProvider";
import { graphql } from "~/lib/graphql-client";
import { mapWithConcurrency } from "~/utils/concurrency";
import { fetchConversationActivity } from "./conversationActivity";
import {
  type ConversationProgress,
  findRunningToolStep,
  replayProgress,
  useConversationProgress,
} from "./useConversationProgress";

export interface ToolStep {
  callId?: string;
  tool: string;
  label: string;
  status: "running" | "done";
  result?: string;
  durationMs?: number;
  modelTurnNumber?: number;
}

export interface AttachmentMeta {
  filename: string;
  content_type: string;
  size_bytes: number;
  s3_bucket: string;
  s3_key: string;
  /** "long_message": the backend's S3 copy of an over-threshold message's
   *  text, shown via "See more" rather than as a file chip. */
  source?: string;
  /** Local preview URL for images (created via URL.createObjectURL) */
  previewUrl?: string;
}

/**
 * An attachment staged in the composer. Nothing is uploaded while it sits
 * here — the file is held locally and only pushed to S3 when the message is
 * actually sent, so removing it costs nothing. The preview is a local object
 * URL, so it renders immediately with no network round-trip.
 */
export interface PendingAttachment {
  /** Stable identity for React keys and removal. */
  uploadId: string;
  file: File;
  filename: string;
  content_type: string;
  size_bytes: number;
  previewUrl?: string;
}

export interface ChatMessage {
  messageId?: string;
  /** Stable row identity from the first streamed token through persistence. */
  renderKey?: string;
  /** Matches a local SSE answer to its completion checkpoint. */
  responseId?: string;
  conversationId: string;
  role: "user" | "assistant";
  content: string;
  timestamp: Date;
  toolSteps?: ToolStep[];
  attachments?: AttachmentMeta[];
  /** True when content was auto-generated (e.g., filenames for attachment-only messages) */
  isContentSynthetic?: boolean;
  /** True when the user stopped this run mid-stream (may have empty content). */
  stopped?: boolean;
  /**
   * Typed data blocks this reply's markers resolve against, captured when
   * the message finalized. Per-message rather than per-conversation: block
   * keys restart at "t1" every request, so a shared map would resolve an
   * older message's marker to a newer turn's block.
   */
  dataBlocks?: DataBlockMap;
  /** Next user message Indy suggested, offered as ghost text in the composer. */
  suggestedUserPrompt?: string;
}

export interface ConversationSummary {
  conversationId: string;
  title: string;
  lastMessageAt: Date;
}

type StreamingPhase =
  | "idle"
  | "fetching_context"
  | "generating"
  | "tool_executing"
  | "done"
  | "error";

interface SSEFrame {
  event: string;
  data: string;
}

// Typed SSE event payloads (mirrors Python models in services/agent_chat/models.py)
interface SuggestedPromptEventData {
  content: string;
}

interface TokenEventData {
  content: string;
}

interface StatusEventData {
  phase: StreamingPhase;
  message: string;
}

interface ErrorEventData {
  code: string;
  message: string;
  partial: boolean;
}

export interface DataBlockEventData {
  key: string;
  marker: string;
  content: string;
  tool: string;
  /**
   * Renderer selector. Absent on events from a server that predates typed
   * blocks, hence optional — read it through {@link dataBlockKind}.
   */
  kind?: string;
  /** Kind-specific display payload; null/absent for markdown blocks. */
  data?: Record<string, unknown> | null;
}

export type DataBlockMap = ReadonlyMap<string, DataBlockEventData>;

/** The default kind: "splice `content` into the text as markdown". */
export const MARKDOWN_BLOCK_KIND = "markdown";

export function dataBlockKind(block: DataBlockEventData): string {
  return block.kind ?? MARKDOWN_BLOCK_KIND;
}

// Complete marker tokens; leftovers with no emitted block are stripped at
// `done` (model imitating history markers, or a typo'd index).
const DATA_MARKER_RE = /\[data:(t\d+)\]\n?/g;

/**
 * Replace markdown data-block markers with their content (mirrors the
 * server-side splice in services/agent/tools/data_blocks.py). Unreferenced
 * blocks are dropped — omitting the marker is the model's deliberate choice
 * to present a reshaped view (e.g. a skill's custom format).
 *
 * Typed blocks are deliberately left alone: their marker stays in the text
 * as a *position*, and `ChatMessageBody` swaps it for a component at render
 * time. Splicing their markdown fallback here would throw the structure
 * away before it ever reached the renderer.
 */
export function spliceDataBlocks(text: string, blocks: DataBlockMap): string {
  let out = text;
  for (const block of blocks.values()) {
    if (dataBlockKind(block) !== MARKDOWN_BLOCK_KIND) continue;
    out = out.split(block.marker).join(block.content);
  }
  // Streaming shows a marker briefly until its block splices; only a
  // finalizing caller strips leftovers.
  return out;
}

/** Final pass at `done`: splice markdown blocks, then strip marker tokens
 * that never got a block so they can't reach the user. A typed block's
 * marker survives — it still has a component to render. */
export function finalizeDataBlocks(text: string, blocks: DataBlockMap): string {
  return spliceDataBlocks(text, blocks).replace(DATA_MARKER_RE, (match, key) =>
    blocks.has(key as string) ? match : "",
  );
}

/**
 * Rebuild a persisted reply's render state from `ChatbotMessage.dataBlocks`
 * (written by `build_persisted_blocks` server-side).
 *
 * Returns the unspliced text plus its block map, so a reloaded conversation
 * renders the same components the live stream did. Returns null whenever
 * there is nothing usable — field absent (every message predating it, and
 * every reply with no typed block), unparseable, or an unknown version — and
 * the caller falls back to the stored markdown, which is exactly today's
 * behavior.
 */
export function parsePersistedDataBlocks(
  raw: unknown,
): { text: string; blocks: DataBlockMap } | null {
  if (!raw) return null;
  try {
    // AppSync hands back an AWSJSON field as a JSON-encoded string. Objects
    // are accepted too so the one link in this chain that can't be exercised
    // from a test — the live wire shape — can't break the render either way.
    const parsed = (typeof raw === "string" ? JSON.parse(raw) : raw) as {
      v?: number;
      text?: unknown;
      blocks?: unknown;
    };
    if (parsed.v !== 1) return null;
    if (typeof parsed.text !== "string" || !Array.isArray(parsed.blocks)) {
      return null;
    }
    const blocks = new Map<string, DataBlockEventData>();
    for (const entry of parsed.blocks) {
      const block = entry as Partial<DataBlockEventData> | null;
      if (
        !block ||
        typeof block.key !== "string" ||
        typeof block.marker !== "string" ||
        typeof block.content !== "string"
      ) {
        return null;
      }
      blocks.set(block.key, block as DataBlockEventData);
    }
    return { text: parsed.text, blocks };
  } catch {
    return null;
  }
}

/**
 * Splice EVERY block's markdown, typed ones included, and strip leftover
 * markers — the exact behavior of `splice_data_blocks` on the server.
 *
 * This is the flattened form of a reply: what got persisted, what history
 * will show, what Slack would post. Used for copy-to-clipboard, so pasted
 * text carries a card's rows instead of the DOM scaffolding under it.
 */
export function spliceAllDataBlocks(
  text: string,
  blocks: DataBlockMap,
): string {
  let out = text;
  for (const block of blocks.values()) {
    out = out.split(block.marker).join(block.content);
  }
  return out.replace(DATA_MARKER_RE, "");
}

// ── Attachment constants ──

const ALLOWED_IMAGE_TYPES = new Set([
  "image/png",
  "image/jpeg",
  "image/gif",
  "image/webp",
]);
const ALLOWED_DOCUMENT_TYPES = new Set([
  "application/pdf",
  "text/plain",
  "text/csv",
]);
const ALLOWED_ATTACHMENT_TYPES = new Set([
  ...ALLOWED_IMAGE_TYPES,
  ...ALLOWED_DOCUMENT_TYPES,
]);
export const ACCEPTED_FILE_TYPES = [...ALLOWED_ATTACHMENT_TYPES].join(",");

const MAX_IMAGE_SIZE = 10 * 1024 * 1024; // 10 MB
const MAX_DOCUMENT_SIZE = 200 * 1024 * 1024; // 200 MB
const MAX_ATTACHMENTS = 5;
/** Attachments upload in parallel at send time, capped so a batch of large
 *  files doesn't saturate the connection. */
const MAX_UPLOAD_CONCURRENCY = 4;

const PRESIGN_BATCH_SIZE = 25;

/**
 * Batch-resolve presigned GET URLs for image attachments in historical messages.
 * Chunks into batches of 25 to stay within the backend limit.
 */
async function resolveImagePreviews(
  messages: ChatMessage[],
): Promise<ChatMessage[]> {
  const keys: { s3_bucket: string; s3_key: string }[] = [];
  for (const msg of messages) {
    for (const att of msg.attachments ?? []) {
      if (att.s3_key && ALLOWED_IMAGE_TYPES.has(att.content_type)) {
        keys.push({ s3_bucket: att.s3_bucket, s3_key: att.s3_key });
      }
    }
  }
  if (keys.length === 0) return messages;

  try {
    const idToken = await getAuthToken();

    // Chunk keys into batches and resolve in parallel
    const chunks: { s3_bucket: string; s3_key: string }[][] = [];
    for (let i = 0; i < keys.length; i += PRESIGN_BATCH_SIZE) {
      chunks.push(keys.slice(i, i + PRESIGN_BATCH_SIZE));
    }

    const results = await Promise.all(
      chunks.map(async (chunk) => {
        const resp = await fetch(getAgentChatUrl("attachment/presign-read"), {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${idToken}`,
          },
          body: JSON.stringify({ keys: chunk }),
        });
        if (!resp.ok) return {};
        const { urls } = (await resp.json()) as {
          urls: Record<string, string>;
        };
        return urls;
      }),
    );

    const urls = Object.assign({}, ...results) as Record<string, string>;

    return messages.map((msg) => ({
      ...msg,
      attachments: msg.attachments?.map((att) => ({
        ...att,
        previewUrl: urls[att.s3_key] ?? att.previewUrl,
      })),
    }));
  } catch (e) {
    console.warn("[resolveImagePreviews] Failed to resolve image previews:", e);
    return messages;
  }
}

const LONG_MESSAGE_RETRY_MS = 2000;

/**
 * Short-lived GET URLs for chat S3 objects, keyed by `s3_key`. The endpoint
 * decides what may be read and silently omits the rest; at most 25 keys.
 */
export async function presignReadUrls(
  keys: { s3_bucket: string; s3_key: string }[],
  signal?: AbortSignal,
): Promise<Record<string, string>> {
  const resp = await fetch(getAgentChatUrl("attachment/presign-read"), {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      Authorization: `Bearer ${await getAuthToken()}`,
    },
    body: JSON.stringify({ keys }),
    signal,
  });
  if (!resp.ok) throw new Error(`presign-read failed (${resp.status})`);
  const { urls } = (await resp.json()) as { urls: Record<string, string> };
  return urls;
}

/** Full text of a long message, read from its S3 copy. */
export async function fetchLongMessageText(
  attachment: AttachmentMeta,
): Promise<string> {
  const urls = await presignReadUrls([
    { s3_bucket: attachment.s3_bucket, s3_key: attachment.s3_key },
  ]);
  const url = urls[attachment.s3_key];
  if (!url) throw new Error("The full message is unavailable");
  let file = await fetch(url);
  if (file.status === 403 || file.status === 404) {
    // The row is saved while the backend upload is still in flight; a reload
    // in that window can beat the object to S3. One short retry covers it.
    await new Promise((resolve) => setTimeout(resolve, LONG_MESSAGE_RETRY_MS));
    file = await fetch(url);
  }
  if (!file.ok) throw new Error(`Full message fetch failed (${file.status})`);
  return file.text();
}

const MODEL = "gpt-6.1-sol";

export type ReasoningEffort = "low" | "medium" | "high" | "xhigh";

export const REASONING_EFFORT_OPTIONS: {
  value: ReasoningEffort;
  label: string;
}[] = [
  { value: "low", label: "Low" },
  { value: "medium", label: "Medium" },
  { value: "high", label: "High" },
  { value: "xhigh", label: "Extra High" },
];

export interface SendMessageOptions {
  /** Content was auto-generated (e.g. filenames for an attachment-only send). */
  isContentSynthetic?: boolean;
}

export interface UseAgentChatReturn {
  messages: ChatMessage[];
  conversationId: string;
  streamingContent: string;
  streamingMessageKey?: string;
  /** Blocks emitted so far by the in-flight reply, for `streamingContent`. */
  streamingDataBlocks: DataBlockMap;
  streamingToolSteps: ToolStep[];
  phase: StreamingPhase;
  isStreaming: boolean;
  isCheckingStatus?: boolean;
  rejectedMessage?: ChatMessage | null;
  isLoadingHistory: boolean;
  error: string | null;
  errorSource: "load" | "stream" | "upload" | null;
  warning: string | null;
  reasoningEffort: ReasoningEffort;
  setReasoningEffort: (effort: ReasoningEffort) => void;
  webSearchEnabled: boolean;
  setWebSearchEnabled: (enabled: boolean) => void;
  /**
   * Uploads whatever is staged in the composer, hangs it off `userMessage`,
   * then streams the reply. Resolves false if the upload failed, so the caller
   * can keep the composer contents for a retry.
   */
  sendMessage: (
    userMessage: string,
    options?: SendMessageOptions,
  ) => Promise<boolean>;
  editMessage: (index: number, newContent: string) => void;
  stopStreaming: () => void;
  startNewChat: () => void;
  pendingAttachments: PendingAttachment[];
  isUploading: boolean;
  addAttachment: (file: File) => void;
  removeAttachment: (index: number) => void;
  conversations: ConversationSummary[];
  isLoadingConversations: boolean;
  hasMoreConversations: boolean;
  loadMoreConversations: () => void;
  selectConversation: (conversationId: string) => void;
  renameConversation: (conversationId: string, newName: string) => void;
  archiveConversation: (conversationId: string) => void;
  retryLoad: () => void;
}

/**
 * Parse SSE frames from a text buffer.
 * SSE format: "event: <type>\ndata: <json>\n\n"
 * Returns parsed frames and any remaining incomplete buffer.
 */
function parseSSEFrames(buffer: string): {
  events: SSEFrame[];
  remainder: string;
} {
  const events: SSEFrame[] = [];
  // Split on double newline (SSE frame delimiter)
  const parts = buffer.split("\n\n");
  // Last part may be incomplete — keep as remainder
  const remainder = parts.pop() ?? "";

  for (const part of parts) {
    const trimmed = part.trim();
    if (!trimmed) continue;

    let event = "";
    let data = "";

    for (const line of trimmed.split("\n")) {
      if (line.startsWith("event: ")) {
        event = line.slice(7);
      } else if (line.startsWith("data: ")) {
        data = line.slice(6);
      }
    }

    if (event && data) {
      events.push({ event, data });
    }
  }

  return { events, remainder };
}

/**
 * Parse a `skill_invoked` SSE data payload into a completed `ToolStep`.
 *
 * Mirrors `SkillInvokedEvent` in services/agent_chat/models.py and the
 * `_emit_skill_invoked` callback in orchestrator.py. The chip is marked
 * `done` immediately because the skill body was rendered server-side as
 * part of the tool result; nothing else is in flight.
 */
export function parseSkillInvokedFrame(data: string): ToolStep {
  const payload = JSON.parse(data) as {
    name: string;
    label: string;
    call_id?: string;
    model_turn_number?: number | null;
  };
  return {
    ...(payload.call_id ? { callId: payload.call_id } : {}),
    modelTurnNumber: payload.model_turn_number ?? undefined,
    tool: `skill:${payload.name}`,
    label: payload.label,
    status: "done",
  };
}

const SKILL_MUTATE_ACTIONS = ["created", "updated", "archived"] as const;
type SkillMutateAction = (typeof SKILL_MUTATE_ACTIONS)[number];

interface SkillMutatedPayload {
  action: SkillMutateAction;
  name: string;
}

function isSkillMutateAction(value: unknown): value is SkillMutateAction {
  return (
    typeof value === "string" &&
    (SKILL_MUTATE_ACTIONS as readonly string[]).includes(value)
  );
}

/** Parse a `skill_mutated` SSE frame; throws on unknown action so the caller's try/catch drops it. */
export function parseSkillMutatedFrame(data: string): SkillMutatedPayload {
  const payload = JSON.parse(data) as { action?: unknown; name?: unknown };
  if (!isSkillMutateAction(payload.action)) {
    throw new Error(`Unknown skill_mutated action: ${String(payload.action)}`);
  }
  if (typeof payload.name !== "string") {
    throw new Error("skill_mutated payload missing name");
  }
  return { action: payload.action, name: payload.name };
}

async function getAuthToken(): Promise<string> {
  // User-pool-only ID token — no Identity Pool call, so it can't trip the
  // credential throttle. See ~/auth/authToken.
  return getIdToken();
}

function getAgentChatUrl(path: string = ""): string {
  const base = getConfig().AGENT_CHAT_FUNCTION_URL.replace(/\/$/, "");
  return path ? `${base}/${path}` : `${base}/`;
}

/**
 * The chat error for a refused turn. A 403 carries a user-facing message from
 * the backend's admission check (e.g. a doctor removed from the Indy cohort
 * mid-session); anything else keeps the generic status line.
 */
async function rejectedTurnMessage(response: Response): Promise<string> {
  const fallback = `Request failed with status ${response.status}`;
  if (response.status !== 403) return fallback;
  try {
    const body: unknown = await response.json();
    if (
      typeof body === "object" &&
      body !== null &&
      "message" in body &&
      typeof body.message === "string"
    ) {
      return body.message;
    }
  } catch {
    // Not JSON (e.g. a gateway page): keep the generic line.
  }
  return fallback;
}

let lastWarmupTime = 0;
const WARMUP_COOLDOWN_MS = 5 * 60 * 1000; // 5 min — matches Lambda freeze timeout

export function warmupAgentChat(): void {
  const now = Date.now();
  if (now - lastWarmupTime < WARMUP_COOLDOWN_MS) return;
  lastWarmupTime = now;
  fetch(getAgentChatUrl("warmup"), {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: "{}",
  }).catch(() => {
    /* error intentionally ignored */
  });
}

/**
 * Build a sorted list of conversation summaries from raw messages.
 * Uses a persistent Map so that incremental page loads enrich the list.
 * Each conversation's title is the earliest user message seen so far.
 */
export function buildConversationSummaries(
  msgs: ChatMessage[],
  dataMap: Map<
    string,
    { title: string; titleTime: number; latestTime: number }
  >,
): ConversationSummary[] {
  for (const msg of msgs) {
    const time = msg.timestamp.getTime();
    const entry = dataMap.get(msg.conversationId);
    if (!entry) {
      dataMap.set(msg.conversationId, {
        title: msg.role === "user" ? msg.content : "",
        titleTime: msg.role === "user" ? time : Infinity,
        latestTime: time,
      });
    } else {
      if (msg.role === "user" && time < entry.titleTime) {
        entry.title = msg.content;
        entry.titleTime = time;
      }
      if (time > entry.latestTime) {
        entry.latestTime = time;
      }
    }
  }
  return Array.from(dataMap.entries())
    .map(([convId, data]) => ({
      conversationId: convId,
      title: data.title || "New conversation",
      lastMessageAt: new Date(data.latestTime),
    }))
    .sort((a, b) => b.lastMessageAt.getTime() - a.lastMessageAt.getTime());
}

// ── Conversation cache ──────────────────────────────────────────────
// Prevents re-fetching when switching between conversations in the sidebar.
// Stale entries (older than TTL) are background-refreshed while showing
// the cached version instantly.

const CACHE_TTL_MS = 15 * 60 * 1000; // 15 minutes

interface CachedConversation {
  messages: ChatMessage[];
  fetchedAt: number; // Date.now() when last fetched from server
}

/**
 * Reconcile locally-held messages with a fresh server fetch.
 * Server messages are authoritative; any local-only messages (not yet
 * persisted) that are newer than the server's latest are appended so
 * they don't vanish from the UI while the backend catches up.
 */
export function reconcileMessages(
  localMessages: ChatMessage[],
  serverMessages: ChatMessage[],
): ChatMessage[] {
  if (localMessages.length === 0) return serverMessages;
  if (serverMessages.length === 0) return localMessages;

  const serverIds = new Set(
    serverMessages.filter((m) => m.messageId).map((m) => m.messageId),
  );

  // Local messages without a server-side messageId haven't been persisted yet
  const localOnly = localMessages.filter(
    (m) => !m.messageId || !serverIds.has(m.messageId),
  );
  if (localOnly.length === 0) return serverMessages;

  // Only keep local-only messages newer than the latest server message
  const latestServerTime = Math.max(
    ...serverMessages.map((m) => m.timestamp.getTime()),
  );
  const unsyncedLocal = localOnly.filter(
    (m) => m.timestamp.getTime() > latestServerTime,
  );
  if (unsyncedLocal.length === 0) return serverMessages;

  return [...serverMessages, ...unsyncedLocal];
}

/** Keep a completed answer available when its durable history write failed. */
export function recoverCheckpointAnswer(
  messages: ChatMessage[],
  conversationId: string,
  status: ConversationProgress,
): ChatMessage[] {
  const startedAt = (status.started_at ?? 0) * 1000;
  if (
    messages.some(
      (m) => m.role === "assistant" && m.timestamp.getTime() >= startedAt,
    )
  )
    return messages;
  const replayed = replayProgress(status);
  if (!replayed.content) return messages;
  return [
    ...messages,
    {
      conversationId,
      role: "assistant",
      content: finalizeDataBlocks(replayed.content, replayed.blocks),
      timestamp: new Date(startedAt),
      dataBlocks: replayed.blocks,
      toolSteps: replayed.steps,
    },
  ];
}

export interface UseAgentChatOptions {
  indigoDocId?: string | null;
  /** Fired when the orchestrator emits a `skill_mutated` SSE event. */
  onSkillMutated?: () => void;
}

/**
 * React hook for consuming the agent chat SSE stream with persistent
 * chat history. Loads previous messages on mount, supports scroll-up
 * pagination, and persists new messages via the backend.
 *
 * Also manages a conversation sidebar: discovers conversations from the
 * message history, supports incremental loading, and per-conversation
 * message fetching via the chatbotMessagesByConversationId GSI.
 */
export function useAgentChat(
  appointmentId: string | null,
  isOpen: boolean,
  options: UseAgentChatOptions = {},
): UseAgentChatReturn {
  const { authUser } = useAuthSession();
  const { isDemo } = useCurrentUser();
  const userId = authUser?.userId ?? null;
  const [lastConversationId, setLastConversationAtom] = useAtom(
    chatbotLastConversationIdAtom,
  );

  const setLastConversationId = useCallback(
    (id: string | null) => {
      setLastConversationAtom(id);
      if (userId) {
        try {
          if (id) sessionStorage.setItem(`indy-conversation:${userId}`, id);
          else sessionStorage.removeItem(`indy-conversation:${userId}`);
        } catch {
          /* Session storage may be disabled. */
        }
      }
    },
    [userId, setLastConversationAtom],
  );

  const [messages, setMessages] = useState<ChatMessage[]>([]);
  /**
   * Mirrors `messages` for reads that happen after an await. `sendMessage`
   * uploads attachments before it streams, and anything that changed the list
   * during that window (a background history refresh) would be
   * discarded if we replayed the snapshot captured at call time.
   */
  const messagesRef = useRef<ChatMessage[]>([]);
  // Layout effect, not passive: the composer's queue sends from a passive
  // effect in a child, and child effects run before this hook's. A passive
  // sync would hand that send the list from before the reply was finalized,
  // and the append would drop the answer that just landed.
  useLayoutEffect(() => {
    messagesRef.current = messages;
  }, [messages]);
  const [conversationId, setConversationId] = useState<string>(() =>
    crypto.randomUUID(),
  );
  const [reasoningEffort, setReasoningEffort] =
    useState<ReasoningEffort>("medium");
  const [webSearchPreferences, setWebSearchPreferences] = useAtom(
    indyWebSearchPreferencesAtom,
  );
  const webSearchEnabled = webSearchPreferences[userId ?? ""] !== false;
  const setWebSearchEnabled = useCallback(
    (enabled: boolean) => {
      if (!userId) return;
      setWebSearchPreferences((previous) => ({
        ...previous,
        [userId]: enabled,
      }));
    },
    [userId, setWebSearchPreferences],
  );
  const [streamingContent, setStreamingContent] = useState("");
  const [streamingMessageKey, setStreamingMessageKey] = useState<string>();
  const completedLocalConversationRef = useRef<string | null>(null);
  // Mirrors dataBlocksRef so a typed block arriving mid-stream re-renders
  // the in-flight message; the ref stays the write path (SSE handlers run
  // outside React's batching).
  const [streamingDataBlocks, setStreamingDataBlocks] = useState<DataBlockMap>(
    () => new Map(),
  );
  const [streamingToolSteps, setStreamingToolSteps] = useState<ToolStep[]>([]);
  const [phase, setPhase] = useState<StreamingPhase>("idle");
  const [isStreaming, setIsStreaming] = useState(false);
  /**
   * Conversation whose reply text is complete (`done` seen) but whose stream
   * has not closed. The server is still saving the answer and releasing the
   * turn lock in that window, so a request would be refused as busy — but it
   * is our own turn, not a remote one, and the close is coming.
   */
  const [closingTurn, setClosingTurn] = useState<string | null>(null);
  /**
   * A send accepted while the turn cannot start yet — during `closingTurn`,
   * or before the status check on opening has answered: already on screen,
   * request held until the way is clear. Reads as streaming from outside so
   * nothing else sends.
   */
  const [deferredSend, setDeferredSend] = useState<{
    conversationId: string;
    message: ChatMessage;
  } | null>(null);
  /** Mirror for the SSE reader, which closes outside React's render. */
  const deferredSendRef = useRef(deferredSend);
  deferredSendRef.current = deferredSend;
  const [rejectedMessage, setRejectedMessage] = useState<ChatMessage | null>(
    null,
  );
  const [error, setError] = useState<string | null>(null);
  const [errorSource, setErrorSource] = useState<
    "load" | "stream" | "upload" | null
  >(null);
  const [warning, setWarning] = useState<string | null>(null);

  // Per-conversation message pagination
  const [isLoadingHistory, setIsLoadingHistory] = useState(false);

  // Conversation sidebar state
  const [conversations, setConversations] = useState<ConversationSummary[]>([]);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [hasMoreConversations, setHasMoreConversations] = useState(true);
  const conversationsNextCursorRef = useRef<string | null>(null);
  const conversationCacheRef = useRef(new Map<string, CachedConversation>());

  // Attachment upload state
  const [pendingAttachments, setPendingAttachments] = useState<
    PendingAttachment[]
  >([]);
  const uploadSeqRef = useRef(0);
  const pendingCountRef = useRef(0);
  const [isUploading, setIsUploading] = useState(false);

  const revokePendingPreviews = useCallback(() => {
    setPendingAttachments((prev) => {
      prev.forEach((a) => {
        if (a.previewUrl) URL.revokeObjectURL(a.previewUrl);
      });
      pendingCountRef.current = 0;
      return [];
    });
  }, []);

  const revokeMessagePreviews = useCallback(() => {
    setMessages((prev) => {
      prev.forEach((m) =>
        m.attachments?.forEach((a) => {
          // Only revoke blob: URLs (from createObjectURL), not presigned S3 URLs
          if (a.previewUrl?.startsWith("blob:"))
            URL.revokeObjectURL(a.previewUrl);
        }),
      );
      return [];
    });
  }, []);

  const hasLoadedMessagesRef = useRef(false);
  const prevOpenRef = useRef(false);
  const abortControllerRef = useRef<AbortController | null>(null);
  const streamingContentRef = useRef("");
  const streamingToolStepsRef = useRef<ToolStep[]>([]);
  const dataBlocksRef = useRef<Map<string, DataBlockEventData>>(new Map());
  const previousResponseIdRef = useRef<string | null>(null);
  const conversationIdRef = useRef(conversationId);
  conversationIdRef.current = conversationId;
  const onSkillMutatedRef = useRef(options.onSkillMutated);
  onSkillMutatedRef.current = options.onSkillMutated;

  // ── Fetch conversations from ChatbotConversation table (for sidebar) ──

  const fetchConversations = useCallback(
    async (cursor?: string | null) => {
      if (!userId) return null;

      const response = await graphql({
        query: chatbotConversationsByUserUpdatedAt,
        variables: {
          userId,
          sortDirection: ModelSortDirection.DESC,
          filter: { archived: { ne: true } },
          limit: 10,
          ...(cursor ? { nextToken: cursor } : {}),
        },
        authMode: "userPool",
      });

      const connection = response.data?.chatbotConversationsByUserUpdatedAt;
      if (!connection) return null;

      const items = (connection.items ?? []).filter(
        (item): item is NonNullable<typeof item> => item !== null,
      );

      const summaries: ConversationSummary[] = items.map((item) => ({
        conversationId: item.conversationId,
        title: item.name || "New conversation",
        lastMessageAt: new Date(item.updatedAt || item.createdAt),
      }));

      return {
        conversations: summaries,
        nextToken: connection.nextToken ?? null,
      };
    },
    [userId],
  );

  // ── Fetch messages for a specific conversation (via GSI) ──

  // The whole conversation, not a page: the composer's Up-arrow recall counts
  // and walks every user message, so it needs all of them loaded.
  const fetchConversationMessages = useCallback(
    async (convId: string) => {
      type Connection =
        ChatbotMessagesByConversationIdWithAttachmentsResult["chatbotMessagesByConversationId"];
      const items: NonNullable<NonNullable<Connection>["items"][number]>[] = [];
      let cursor: string | null = null;
      // Sequential by necessity: each page's cursor comes from the last one.
      do {
        const variables: ChatbotMessagesByConversationIdQueryVariables = {
          conversationId: convId,
          sortDirection: ModelSortDirection.DESC,
          limit: 100,
          ...(cursor ? { nextToken: cursor } : {}),
        };
        const { data } = await graphql({
          query: chatbotMessagesByConversationIdWithAttachments,
          variables,
          authMode: "userPool",
        });
        const connection: Connection | undefined =
          data?.chatbotMessagesByConversationId;
        if (!connection) return null;
        for (const item of connection.items ?? []) {
          if (item !== null) items.push(item);
        }
        cursor = connection.nextToken ?? null;
      } while (cursor);

      let activity = new Map<string, ToolStep[]>();
      try {
        activity = await fetchConversationActivity(
          getAgentChatUrl("conversation/status"),
          convId,
          await getAuthToken(),
        );
      } catch {
        // Keep the answer readable if the optional activity read fails.
        if (conversationIdRef.current === convId) {
          setWarning(
            "Earlier activity could not be loaded. Reopen the conversation to retry.",
          );
        }
      }
      // DESC returns newest first; reverse for oldest-at-top rendering
      const chatMessages: ChatMessage[] = items.reverse().map((m) => {
        // A reply that carried entity components round-trips through
        // dataBlocks; everything else keeps the stored markdown.
        const persisted = parsePersistedDataBlocks(m.dataBlocks);
        return {
          messageId: m.messageId,
          toolSteps: activity.get(m.messageId),
          conversationId: m.conversationId,
          role: m.role as "user" | "assistant",
          content: persisted?.text ?? m.content,
          ...(persisted ? { dataBlocks: persisted.blocks } : {}),
          timestamp: new Date(m.createdAt),
          stopped: m.userPaused === true,
          ...(m.suggestedUserPrompt
            ? { suggestedUserPrompt: m.suggestedUserPrompt }
            : {}),
          ...(m.attachments?.length
            ? {
                attachments: m.attachments
                  .filter(
                    (a): a is NonNullable<typeof a> =>
                      a !== null && a !== undefined,
                  )
                  .map((a) => ({
                    filename: a.filename,
                    content_type: a.contentType,
                    size_bytes: a.sizeBytes,
                    s3_bucket: a.s3Bucket ?? "",
                    s3_key: a.s3Key ?? "",
                    ...(a.source ? { source: a.source } : {}),
                  })),
              }
            : {}),
        };
      });

      return chatMessages;
    },
    [], // graphql is a module-level import, not a hook dependency
  );

  const fetchTurnStatus = useCallback(
    async (id: string, signal: AbortSignal): Promise<ConversationProgress> => {
      const response = await fetch(getAgentChatUrl("conversation/status"), {
        method: "POST",
        signal,
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getAuthToken()}`,
        },
        body: JSON.stringify({ conversation_id: id }),
      });
      // Allow frontend/backend rollouts to happen separately. An older
      // Lambda has no status route and keeps the original direct-SSE UX.
      if (response.status === 404)
        return {
          active: false,
          turn_id: null,
          started_at: null,
          events: [],
          user_message_id: null,
          assistant_message_id: null,
        };
      if (!response.ok) throw new Error("Conversation status unavailable");
      return response.json() as Promise<ConversationProgress>;
    },
    [],
  );

  const refreshCompletedTurn = useCallback(
    async (id: string, status: ConversationProgress) => {
      if (conversationIdRef.current !== id) return false;
      const failure = status.events.find((event) => event.event === "error");
      if (failure) {
        setError(
          String(
            failure.data.message ?? "Indy could not finish this response.",
          ),
        );
        setErrorSource("stream");
      }
      const responseId = status.events.find((event) => event.event === "done")
        ?.data.response_id;
      const localAnswer =
        typeof responseId === "string" && responseId
          ? messagesRef.current.find(
              (m) => m.conversationId === id && m.responseId === responseId,
            )
          : undefined;
      if (localAnswer) {
        // The original SSE tab already has the complete answer, tool steps
        // and loaded images. Only attach the durable ID; recovery tabs still
        // fetch history below. While active, don't reload the local input.
        if (!status.active && status.assistant_message_id) {
          setMessages((prev) =>
            prev.map((m) =>
              m === localAnswer
                ? { ...m, messageId: status.assistant_message_id ?? undefined }
                : m,
            ),
          );
        } else if (!status.active) {
          setWarning(
            "This response could not be saved. Copy anything you need before leaving this conversation.",
          );
        }
        if (!status.active) completedLocalConversationRef.current = null;
        return true;
      }
      if (
        !status.active &&
        !status.assistant_message_id &&
        replayProgress(status).content
      ) {
        // A missing ID is not successful persistence. Preserve the direct
        // SSE answer, or recover it from the checkpoint after a refresh.
        setWarning(
          "This response could not be saved. Copy anything you need before leaving this conversation.",
        );
        setMessages((prev) => recoverCheckpointAnswer(prev, id, status));
        return true;
      }
      const hasExpectedMessage = (m: ChatMessage) =>
        status.active
          ? m.role === "user" &&
            (status.user_message_id
              ? m.messageId === status.user_message_id
              : m.timestamp.getTime() >= (status.started_at ?? 0) * 1000)
          : m.messageId === status.assistant_message_id;
      if (
        (status.active || status.assistant_message_id) &&
        messagesRef.current.some(hasExpectedMessage)
      )
        return true;
      const saved = await fetchConversationMessages(id);
      if (!saved || conversationIdRef.current !== id) return false;
      if (
        (status.active || status.assistant_message_id) &&
        !saved.some(hasExpectedMessage)
      )
        return false;
      const fresh = await resolveImagePreviews(saved);
      if (conversationIdRef.current !== id) return false;
      const first = fresh[0]?.timestamp.getTime() ?? 0;
      setConversations((prev) =>
        prev.some((c) => c.conversationId === id)
          ? prev
          : [
              {
                conversationId: id,
                title:
                  fresh.find((m) => m.role === "user")?.content.slice(0, 100) ||
                  "New conversation",
                lastMessageAt: fresh.at(-1)?.timestamp ?? new Date(),
              },
              ...prev,
            ],
      );
      setMessages((prev) => [
        ...prev.filter((m) => m.messageId && m.timestamp.getTime() < first),
        ...fresh,
      ]);
      conversationCacheRef.current.set(id, {
        messages: fresh,
        fetchedAt: Date.now(),
      });
      if (!status.active) completedLocalConversationRef.current = null;
      return true;
    },
    [fetchConversationMessages],
  );

  const refreshRemoteInput = useCallback(
    async (id: string, status: ConversationProgress) => {
      // The checkpoint can lag SSE done and not contain its response ID yet.
      // Our submitted input is already visible; only joining tabs need it.
      if (completedLocalConversationRef.current === id) return true;
      return refreshCompletedTurn(id, status);
    },
    [refreshCompletedTurn],
  );
  const progress = useConversationProgress({
    conversationId,
    enabled: isOpen && !!userId && !isDemo,
    localStreaming: isStreaming,
    fetchStatus: fetchTurnStatus,
    onCompleted: refreshCompletedTurn,
    onInput: refreshRemoteInput,
  });
  // Our own finishing turn still holds the lock; that is not a remote stream.
  const remoteActive =
    !isStreaming &&
    !!progress.snapshot?.active &&
    closingTurn !== conversationId;
  const replayed =
    remoteActive && progress.snapshot
      ? replayProgress(progress.snapshot)
      : null;
  const remoteHasSavedAnswer =
    !!progress.snapshot?.started_at &&
    messages.some(
      (m) =>
        m.role === "assistant" &&
        m.timestamp.getTime() >= (progress.snapshot?.started_at ?? 0) * 1000,
    );
  const reconnectProgress = progress.reconnect;
  const settleProgress = progress.settle;

  // ── Load conversations + most recent conversation when modal opens ──

  const loadOnOpen = useCallback(async () => {
    warmupAgentChat();
    setIsLoadingConversations(true);
    setError(null);
    setErrorSource(null);

    // Show loading spinner for messages on first open
    if (!hasLoadedMessagesRef.current) {
      setIsLoadingHistory(true);
    }

    let resumeId = lastConversationId;
    if (!resumeId && userId) {
      try {
        resumeId = sessionStorage.getItem(`indy-conversation:${userId}`);
      } catch {
        /* optional storage */
      }
    }
    // If we have a lastConversationId, start fetching its messages in parallel
    const messagesPromise =
      !hasLoadedMessagesRef.current && resumeId
        ? fetchConversationMessages(resumeId)
        : null;

    try {
      // msgResult is undefined when messagesPromise is null (no resumeId) —
      // the null-checks below handle this intentionally.
      const [convResult, msgResult] = await Promise.allSettled([
        fetchConversations(),
        ...(messagesPromise ? [messagesPromise] : []),
      ]);

      // Process conversations list
      const convData =
        convResult.status === "fulfilled" ? convResult.value : null;
      if (convResult.status === "rejected") {
        console.error("Failed to load conversations:", convResult.reason);
      }

      if (convData && convData.conversations.length > 0) {
        conversationsNextCursorRef.current = convData.nextToken;
        setHasMoreConversations(convData.nextToken !== null);
        // GSI returns in DESC order already, no client-side sort needed
        setConversations(convData.conversations);
      }

      // Process pre-fetched messages (from resumeId)
      if (!hasLoadedMessagesRef.current) {
        hasLoadedMessagesRef.current = true;

        const msgData =
          msgResult && msgResult.status === "fulfilled"
            ? msgResult.value
            : null;

        if (msgData) {
          // resumeId is guaranteed non-null here — messagesPromise
          // (which produced msgData) is only created when resumeId is truthy.
          const withPreviews = await resolveImagePreviews(msgData);
          setMessages(withPreviews);
          setConversationId(resumeId!);
          conversationCacheRef.current.set(resumeId!, {
            messages: withPreviews,
            fetchedAt: Date.now(),
          });
        } else {
          // resumeId was set but its fetch failed or returned nothing —
          // clear the stale ID so the next open starts fresh.
          setLastConversationId(null);
        }
      }

      // If both fetches failed, show error
      if (
        convResult.status === "rejected" &&
        (!messagesPromise || msgResult?.status === "rejected")
      ) {
        setError("Failed to load conversations. Please try again.");
        setErrorSource("load");
      }
    } catch (err) {
      console.error("Failed to load data:", err);
      setError("Failed to load conversations. Please try again.");
      setErrorSource("load");
    } finally {
      setIsLoadingConversations(false);
      setIsLoadingHistory(false);
    }
  }, [
    fetchConversations,
    fetchConversationMessages,
    lastConversationId,
    userId,
    setLastConversationId,
  ]);

  useEffect(() => {
    const justOpened = isOpen && !prevOpenRef.current;
    prevOpenRef.current = isOpen;

    if (!justOpened) return;

    void loadOnOpen();
  }, [isOpen, loadOnOpen]);

  // ── Keep cache in sync when messages change (local additions) ──

  useEffect(() => {
    if (messages.length === 0) return;
    const cache = conversationCacheRef.current;
    const existing = cache.get(conversationId);
    cache.set(conversationId, {
      messages,
      fetchedAt: existing?.fetchedAt ?? Date.now(),
    });
  }, [messages, conversationId]);

  // ── Load more conversations for sidebar (scroll pagination) ──

  const loadMoreConversations = useCallback(async () => {
    if (
      !conversationsNextCursorRef.current ||
      isLoadingConversations ||
      !hasMoreConversations
    )
      return;
    setIsLoadingConversations(true);

    try {
      const result = await fetchConversations(
        conversationsNextCursorRef.current,
      );
      if (!result) return;

      conversationsNextCursorRef.current = result.nextToken;
      setHasMoreConversations(result.nextToken !== null);

      if (result.conversations.length > 0) {
        setConversations((prev) => [...prev, ...result.conversations]);
      }
    } catch (err) {
      console.error("Failed to load more conversations:", err);
    } finally {
      setIsLoadingConversations(false);
    }
  }, [isLoadingConversations, hasMoreConversations, fetchConversations]);

  // ── Select a conversation from sidebar ──

  const selectConversation = useCallback(
    async (convId: string) => {
      // Blocked during an upload too: switching revokes the preview URLs of
      // files still being uploaded for the outgoing message.
      if (
        convId === conversationIdRef.current ||
        isStreaming ||
        isUploading ||
        deferredSend
      )
        return;

      setError(null);
      setErrorSource(null);
      setLastConversationId(convId);
      revokePendingPreviews();

      const cache = conversationCacheRef.current;
      const cached = cache.get(convId);

      revokeMessagePreviews();

      if (cached) {
        // Show cached data instantly — no loading spinner
        setMessages(cached.messages);
        setConversationId(convId);

        // Background refresh if stale
        if (Date.now() - cached.fetchedAt > CACHE_TTL_MS) {
          fetchConversationMessages(convId)
            .then(async (result) => {
              if (!result) return;
              // Read live cache to include any messages sent while fetch was in-flight
              const currentCache = cache.get(convId);
              const reconciled = await resolveImagePreviews(
                reconcileMessages(currentCache?.messages ?? [], result),
              );
              cache.set(convId, {
                messages: reconciled,
                fetchedAt: Date.now(),
              });
              // Only update React state if still viewing this conversation
              if (conversationIdRef.current !== convId) return;
              setMessages(reconciled);
            })
            .catch((err) => {
              console.error("Background refresh failed:", err);
            });
        }
      } else {
        // No cache — switch immediately and show loading spinner
        setConversationId(convId);
        setIsLoadingHistory(true);
        try {
          const result = await fetchConversationMessages(convId);
          if (result) {
            const withPreviews = await resolveImagePreviews(result);
            setMessages(withPreviews);
            cache.set(convId, {
              messages: withPreviews,
              fetchedAt: Date.now(),
            });
          }
        } catch (err) {
          console.error("Failed to load conversation:", err);
          setError("Failed to load conversation. Please try again.");
          setErrorSource("load");
        } finally {
          setIsLoadingHistory(false);
        }
      }
    },
    [
      fetchConversationMessages,
      isStreaming,
      isUploading,
      deferredSend,
      setLastConversationId,
      revokePendingPreviews,
      revokeMessagePreviews,
    ],
  );

  // ── Start new conversation ──

  const startNewChat = useCallback(() => {
    // See selectConversation — an in-flight upload owns those preview URLs.
    if (isStreaming || remoteActive || isUploading || deferredSend) return;
    warmupAgentChat();
    const newId = crypto.randomUUID();
    setConversationId(newId);
    setLastConversationId(null);
    setRejectedMessage(null);
    revokeMessagePreviews();
    setStreamingContent("");
    streamingContentRef.current = "";
    previousResponseIdRef.current = null;
    setPhase("idle");
    setError(null);
    setErrorSource(null);
    revokePendingPreviews();
  }, [
    isStreaming,
    remoteActive,
    isUploading,
    deferredSend,
    revokePendingPreviews,
    revokeMessagePreviews,
    setLastConversationId,
  ]);

  // ── Attachment management ──

  const addAttachment = useCallback((file: File) => {
    if (!ALLOWED_ATTACHMENT_TYPES.has(file.type)) {
      setError(
        `Unsupported file type: ${file.type || "unknown"}. Supported: images (PNG, JPEG, GIF, WebP) and documents (PDF, TXT, CSV).`,
      );
      setErrorSource("upload");
      return;
    }

    const maxSize = ALLOWED_IMAGE_TYPES.has(file.type)
      ? MAX_IMAGE_SIZE
      : MAX_DOCUMENT_SIZE;
    if (file.size > maxSize) {
      setError(
        `File too large (${(file.size / (1024 * 1024)).toFixed(1)} MB). Maximum: ${maxSize / (1024 * 1024)} MB.`,
      );
      setErrorSource("upload");
      return;
    }

    if (pendingCountRef.current >= MAX_ATTACHMENTS) {
      setError(`Maximum ${MAX_ATTACHMENTS} attachments per message.`);
      setErrorSource("upload");
      return;
    }

    // Stage locally — no network yet. The upload happens at send time, so an
    // attachment the user adds and then removes never costs an upload.
    pendingCountRef.current += 1;
    setError(null);
    setErrorSource(null);
    setPendingAttachments((prev) => [
      ...prev,
      {
        uploadId: `upload-${(uploadSeqRef.current += 1)}`,
        file,
        filename: file.name,
        content_type: file.type,
        size_bytes: file.size,
        previewUrl: ALLOWED_IMAGE_TYPES.has(file.type)
          ? URL.createObjectURL(file)
          : undefined,
      },
    ]);
  }, []);

  /**
   * Push staged files to S3 and return their metadata. Runs at send time and
   * rejects if any upload fails, so the caller can leave the composer intact
   * for a retry.
   */
  const uploadPending = useCallback(
    async (
      staged: PendingAttachment[],
      conversationId: string,
    ): Promise<AttachmentMeta[]> => {
      const idToken = await getAuthToken();

      return mapWithConcurrency(
        staged,
        MAX_UPLOAD_CONCURRENCY,
        async (p): Promise<AttachmentMeta> => {
          const presignResp = await fetch(getAgentChatUrl("upload/presign"), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body: JSON.stringify({
              filename: p.filename,
              content_type: p.content_type,
              size_bytes: p.size_bytes,
              conversation_id: conversationId,
            }),
          });

          if (!presignResp.ok) {
            const errBody = (await presignResp
              .json()
              .catch(() => ({ message: "Upload failed" }))) as {
              message?: string;
            };
            throw new Error(
              errBody.message || `Presign failed: ${presignResp.status}`,
            );
          }

          const { upload_url, s3_bucket, s3_key } =
            (await presignResp.json()) as {
              upload_url: string;
              s3_bucket: string;
              s3_key: string;
            };

          const putResp = await fetch(upload_url, {
            method: "PUT",
            headers: { "Content-Type": p.content_type },
            body: p.file,
          });
          if (!putResp.ok) {
            throw new Error(`S3 upload failed: ${putResp.status}`);
          }

          return {
            filename: p.filename,
            content_type: p.content_type,
            size_bytes: p.size_bytes,
            s3_bucket,
            s3_key,
            previewUrl: p.previewUrl,
          };
        },
      );
    },
    [],
  );

  const removeAttachment = useCallback((index: number) => {
    setPendingAttachments((prev) => {
      const removed = prev[index];
      if (removed?.previewUrl) {
        URL.revokeObjectURL(removed.previewUrl);
      }
      const next = prev.filter((_, i) => i !== index);
      pendingCountRef.current = next.length;
      return next;
    });
  }, []);

  // ── SSE streaming ──

  const startStream = useCallback(
    (updatedMessages: ChatMessage[]) => {
      setRejectedMessage(null);
      setMessages(updatedMessages);
      setIsStreaming(true);
      setPhase("fetching_context");
      const messageKey = crypto.randomUUID();
      setStreamingMessageKey(messageKey);
      setStreamingContent("");
      streamingContentRef.current = "";
      setStreamingToolSteps([]);
      streamingToolStepsRef.current = [];
      dataBlocksRef.current = new Map();
      setStreamingDataBlocks(dataBlocksRef.current);
      setError(null);
      setErrorSource(null);
      setWarning(null);

      // Abort any in-flight request
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      const currentConvId = conversationIdRef.current;

      void (async () => {
        try {
          const idToken = await getAuthToken();

          // Only send the latest message — OpenAI tracks conversation
          // history server-side via modelConversationId.
          const latestMessage = updatedMessages[updatedMessages.length - 1];
          const messagePayload: Record<string, unknown> = {
            role: latestMessage.role,
            content: latestMessage.content,
          };
          if (latestMessage.attachments?.length) {
            messagePayload.attachments = latestMessage.attachments.map(
              ({ filename, content_type, size_bytes, s3_bucket, s3_key }) => ({
                filename,
                content_type,
                size_bytes,
                s3_bucket,
                s3_key,
                source: "portal",
              }),
            );
          }
          const body = JSON.stringify({
            ...(appointmentId != null ? { appointment_id: appointmentId } : {}),
            ...(options.indigoDocId != null
              ? { indigo_doc_id: options.indigoDocId }
              : {}),
            model: MODEL,
            reasoning_effort: reasoningEffort,
            enable_web_search: webSearchEnabled,
            conversation_id: currentConvId,
            messages: [messagePayload],
            context_profile: "full_context",
            is_demo: isDemo,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          });

          const response = await fetch(getAgentChatUrl(), {
            method: "POST",
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${idToken}`,
            },
            body,
            signal: abortController.signal,
          });

          if (!response.ok) {
            throw new Error(await rejectedTurnMessage(response));
          }

          if (!response.body) {
            throw new Error("Response body is null — streaming not supported");
          }

          const reader = response.body.getReader();
          const decoder = new TextDecoder();
          let buffer = "";
          // A completed turn's stream closes after the server releases the
          // conversation lock, so the close is the earliest safe moment to
          // send again. A stream that ends without `done` (error, busy) says
          // nothing about the lock and leaves the status poll in charge.
          let sawDone = false;
          let doneResponseId: string | undefined;
          let suggestedUserPrompt: string | undefined;

          // eslint-disable-next-line no-constant-condition
          while (true) {
            const { done, value } = await reader.read();
            if (done) break;

            buffer += decoder.decode(value, { stream: true });
            const { events, remainder } = parseSSEFrames(buffer);
            buffer = remainder;

            for (const frame of events) {
              try {
                switch (frame.event) {
                  case "token": {
                    const tokenData = JSON.parse(frame.data) as TokenEventData;
                    setPhase("generating");
                    streamingContentRef.current += tokenData.content;
                    setStreamingContent(
                      spliceDataBlocks(
                        streamingContentRef.current,
                        dataBlocksRef.current,
                      ),
                    );
                    break;
                  }

                  case "status": {
                    const statusData = JSON.parse(
                      frame.data,
                    ) as StatusEventData;
                    setPhase(statusData.phase);
                    break;
                  }

                  case "tool_call": {
                    const toolCallData = JSON.parse(frame.data) as {
                      call_id?: string;
                      model_turn_number?: number | null;
                      tool: string;
                      label: string;
                    };
                    const step: ToolStep = {
                      ...(toolCallData.call_id
                        ? { callId: toolCallData.call_id }
                        : {}),
                      modelTurnNumber:
                        toolCallData.model_turn_number ?? undefined,
                      tool: toolCallData.tool,
                      label: toolCallData.label,
                      status: "running",
                    };
                    streamingToolStepsRef.current = [
                      ...streamingToolStepsRef.current,
                      step,
                    ];
                    setStreamingToolSteps([...streamingToolStepsRef.current]);
                    setPhase("tool_executing");
                    break;
                  }

                  case "tool_result": {
                    const toolResultData = JSON.parse(frame.data) as {
                      call_id?: string;
                      duration_ms?: number | null;
                      model_turn_number?: number | null;
                      tool: string;
                      label: string;
                    };
                    const resultText = toolResultData.label || undefined;
                    const matchedIndex = findRunningToolStep(
                      streamingToolStepsRef.current,
                      toolResultData,
                    );
                    streamingToolStepsRef.current =
                      streamingToolStepsRef.current.map((step, index) =>
                        index === matchedIndex ||
                        (step.tool.startsWith("skill:") &&
                          !!toolResultData.call_id &&
                          step.callId === toolResultData.call_id)
                          ? {
                              ...step,
                              status: "done" as const,
                              result:
                                index === matchedIndex
                                  ? resultText
                                  : step.result,
                              modelTurnNumber:
                                toolResultData.model_turn_number ??
                                step.modelTurnNumber,
                              durationMs:
                                toolResultData.duration_ms ?? undefined,
                            }
                          : step,
                      );
                    setStreamingToolSteps([...streamingToolStepsRef.current]);
                    setPhase("generating");
                    break;
                  }

                  case "data_block": {
                    // Tool-rendered content (e.g. a results table) streamed
                    // directly — arrives before generation; spliced into the
                    // text wherever the model places the marker.
                    const blockData = JSON.parse(
                      frame.data,
                    ) as DataBlockEventData;
                    dataBlocksRef.current = new Map(dataBlocksRef.current).set(
                      blockData.key,
                      blockData,
                    );
                    // New identity so the in-flight message re-renders: a
                    // typed block's marker shows nothing until its block
                    // lands, and the tokens around it may already be on
                    // screen.
                    setStreamingDataBlocks(dataBlocksRef.current);
                    setStreamingContent(
                      spliceDataBlocks(
                        streamingContentRef.current,
                        dataBlocksRef.current,
                      ),
                    );
                    break;
                  }

                  case "suggested_prompt": {
                    // Sent just before `done`; attached to the reply there.
                    suggestedUserPrompt = (
                      JSON.parse(frame.data) as SuggestedPromptEventData
                    ).content;
                    break;
                  }

                  case "done": {
                    const doneData = JSON.parse(frame.data) as {
                      warning?: string | null;
                      response_id?: string;
                    };
                    completedLocalConversationRef.current = currentConvId;
                    sawDone = true;
                    doneResponseId = doneData.response_id;
                    setClosingTurn(currentConvId);
                    // Mirrors the server-side final splice for markdown
                    // blocks. Typed markers deliberately survive here: the
                    // server flattens them to their fallback for storage,
                    // the client keeps them as render positions.
                    const finalContent = finalizeDataBlocks(
                      streamingContentRef.current,
                      dataBlocksRef.current,
                    );
                    const steps = streamingToolStepsRef.current;
                    // Read the ref BEFORE setMessages, like finalContent and
                    // steps: React runs the updater on a later tick, after the
                    // reset below has already swapped in an empty Map. Reading
                    // it inside the closure handed the message no blocks, so
                    // its surviving markers resolved to nothing and the cards
                    // vanished the instant streaming finished.
                    const finalBlocks = dataBlocksRef.current;
                    setMessages((prev) => [
                      ...prev,
                      {
                        conversationId: currentConvId,
                        renderKey: messageKey,
                        responseId: doneData.response_id,
                        role: "assistant",
                        content: finalContent,
                        timestamp: new Date(),
                        toolSteps: steps.length > 0 ? steps : undefined,
                        // Typed markers survive the finalize, so the message
                        // has to carry the blocks they resolve against.
                        dataBlocks: finalBlocks,
                        suggestedUserPrompt,
                      },
                    ]);
                    setStreamingContent("");
                    streamingContentRef.current = "";
                    streamingToolStepsRef.current = [];
                    dataBlocksRef.current = new Map();
                    setStreamingDataBlocks(dataBlocksRef.current);
                    setStreamingToolSteps([]);
                    setIsStreaming(false);
                    setPhase("idle");
                    setWarning(doneData.warning ?? null);
                    reconnectProgress();
                    break;
                  }

                  case "skill_invoked": {
                    // Emitted by the orchestrator when the model calls the
                    // invoke_skill tool (`_emit_skill_invoked` in
                    // services/agent_chat/orchestrator.py). Surface as a
                    // completed tool step so the UI shows "Loaded skill: X"
                    // inline with the run.
                    const step = parseSkillInvokedFrame(frame.data);
                    streamingToolStepsRef.current = [
                      ...streamingToolStepsRef.current,
                      step,
                    ];
                    setStreamingToolSteps([...streamingToolStepsRef.current]);
                    break;
                  }

                  case "skill_mutated": {
                    // Parse to validate; payload is intentionally discarded.
                    parseSkillMutatedFrame(frame.data);
                    onSkillMutatedRef.current?.();
                    break;
                  }

                  case "error": {
                    const errorData = JSON.parse(frame.data) as ErrorEventData;
                    if (errorData.code === "CONVERSATION_BUSY") {
                      setRejectedMessage(updatedMessages.at(-1) ?? null);
                      setMessages(updatedMessages.slice(0, -1));
                      setError(null);
                      setIsStreaming(false);
                      reconnectProgress();
                      return;
                    }
                    setError(errorData.message || "An error occurred");
                    setErrorSource("stream");
                    if (!errorData.partial) {
                      setIsStreaming(false);
                      setPhase("error");
                    }
                    break;
                  }
                }
              } catch {
                console.warn("Failed to parse SSE frame data:", frame.data);
              }
            }
          }

          // If stream ended without a "done" event, finalize
          if (streamingContentRef.current) {
            const remainingContent = finalizeDataBlocks(
              streamingContentRef.current,
              dataBlocksRef.current,
            );
            const remainingSteps = streamingToolStepsRef.current;
            // Read before setMessages — see the `done` handler above.
            const remainingBlocks = dataBlocksRef.current;
            setMessages((prev) => [
              ...prev,
              {
                conversationId: currentConvId,
                renderKey: messageKey,
                role: "assistant",
                content: remainingContent,
                timestamp: new Date(),
                toolSteps:
                  remainingSteps.length > 0 ? remainingSteps : undefined,
                dataBlocks: remainingBlocks,
              },
            ]);
            setStreamingContent("");
            streamingContentRef.current = "";
            streamingToolStepsRef.current = [];
            dataBlocksRef.current = new Map();
            setStreamingDataBlocks(dataBlocksRef.current);
            setStreamingToolSteps([]);
          }
          setIsStreaming(false);
          setPhase("idle");
          if (sawDone) settleProgress(currentConvId);
          if (sawDone && deferredSendRef.current) {
            // The deferred stream will abort the status poll that attaches
            // the saved ID to the reply just finished. Read it once first;
            // the deferred request goes out when `closingTurn` clears. Only
            // the ID attach, never the history reload `refreshCompletedTurn`
            // can fall back to — that would drop the unsaved deferred message.
            try {
              const status = await fetchTurnStatus(
                currentConvId,
                new AbortController().signal,
              );
              const savedId = status.assistant_message_id;
              if (!status.active && savedId && doneResponseId) {
                setMessages((prev) =>
                  prev.map((m) =>
                    m.conversationId === currentConvId &&
                    m.responseId === doneResponseId
                      ? { ...m, messageId: savedId }
                      : m,
                  ),
                );
              }
            } catch {
              // Best effort; the message stays reachable without its ID.
            }
          }
          setClosingTurn(null);
        } catch (err) {
          setClosingTurn(null);
          if (err instanceof DOMException && err.name === "AbortError") {
            return;
          }
          console.error("Agent chat error:", err);
          setError(
            err instanceof Error ? err.message : "An unexpected error occurred",
          );
          setErrorSource("stream");
          setIsStreaming(false);
          setPhase("error");
        }
      })();
    },
    [
      appointmentId,
      options.indigoDocId,
      reasoningEffort,
      webSearchEnabled,
      isDemo,
      reconnectProgress,
      settleProgress,
      fetchTurnStatus,
    ],
  );

  // The close settles the status check in the same batch that clears
  // `closingTurn`, so the request goes out on the first idle render. A send
  // held for the opening check waits for its answer, and for any remote turn
  // that answer reveals.
  useEffect(() => {
    if (
      !deferredSend ||
      closingTurn !== null ||
      progress.checking ||
      remoteActive
    )
      return;
    setDeferredSend(null);
    if (deferredSend.conversationId !== conversationIdRef.current) return;
    // From the live list, not a snapshot: the ID attach above may have
    // landed since the message was shown.
    const { message } = deferredSend;
    startStream([...messagesRef.current.filter((m) => m !== message), message]);
  }, [deferredSend, closingTurn, progress.checking, remoteActive, startStream]);

  const sendMessage = useCallback(
    async (
      userMessage: string,
      { isContentSynthetic }: SendMessageOptions = {},
    ): Promise<boolean> => {
      if (isStreaming || remoteActive || deferredSend) return false;
      // Hold the request, not the message: while our own turn is closing, or
      // while the status check on opening has not answered yet. Refusing
      // here used to leave the first message of a fresh conversation waiting
      // on a poll that cannot say anything but "idle".
      const hold =
        closingTurn === conversationIdRef.current || progress.checking;
      // Pin the conversation before the upload so the attachment is presigned
      // against, and posted to, the same conversation even if the user
      // switches away mid-upload.
      const currentConvId = conversationIdRef.current;

      // Staged files are uploaded here, at send time, rather than when they
      // were added — so nothing is uploaded for an attachment the user removes.
      // Every send carries whatever is staged: the composer's own send and the
      // edit-resend (which appends a new message) are both "the next message".
      const staged = pendingAttachments;
      let attachments: AttachmentMeta[] | undefined =
        rejectedMessage?.conversationId === currentConvId
          ? rejectedMessage.attachments
          : undefined;
      if (staged.length) {
        setIsUploading(true);
        setError(null);
        setErrorSource(null);
        try {
          attachments = await uploadPending(staged, currentConvId);
        } catch (err) {
          console.error("Attachment upload failed:", err);
          setError(
            err instanceof Error ? err.message : "Failed to upload attachment",
          );
          setErrorSource("upload");
          return false;
        } finally {
          setIsUploading(false);
        }
      }

      setLastConversationId(currentConvId);

      // Add or update conversation in sidebar list
      setConversations((prev) => {
        const exists = prev.some((c) => c.conversationId === currentConvId);
        if (exists) {
          return prev
            .map((c) =>
              c.conversationId === currentConvId
                ? { ...c, lastMessageAt: new Date() }
                : c,
            )
            .sort(
              (a, b) => b.lastMessageAt.getTime() - a.lastMessageAt.getTime(),
            );
        }
        return [
          {
            conversationId: currentConvId,
            title: userMessage,
            lastMessageAt: new Date(),
          },
          ...prev,
        ];
      });

      // Read the list *now*, not from the snapshot captured before the upload.
      const message: ChatMessage = {
        conversationId: currentConvId,
        role: "user",
        content: userMessage,
        timestamp: new Date(),
        attachments: attachments?.length ? attachments : undefined,
        isContentSynthetic: isContentSynthetic || undefined,
      };
      if (hold) {
        // Show the message now; the request waits for the way to clear.
        setMessages((prev) => [...prev, message]);
        setDeferredSend({ conversationId: currentConvId, message });
      } else {
        startStream([...messagesRef.current, message]);
      }

      // Drop only what was actually sent — anything staged while the upload
      // was in flight stays in the composer. Preview URLs aren't revoked here;
      // the sent message still needs them for display.
      if (attachments?.length) {
        const sentIds = new Set(staged.map((s) => s.uploadId));
        setPendingAttachments((prev) => {
          const next = prev.filter((p) => !sentIds.has(p.uploadId));
          pendingCountRef.current = next.length;
          return next;
        });
      }
      return true;
    },
    [
      startStream,
      setLastConversationId,
      pendingAttachments,
      uploadPending,
      isStreaming,
      remoteActive,
      progress.checking,
      rejectedMessage,
      deferredSend,
      closingTurn,
    ],
  );

  const editMessage = useCallback(
    (index: number, newContent: string) => {
      startStream([
        ...messages.slice(0, index),
        {
          conversationId: conversationIdRef.current,
          role: "user",
          content: newContent,
          timestamp: new Date(),
        },
      ]);
    },
    [messages, startStream],
  );

  const stopStreaming = useCallback(() => {
    if (deferredSend) {
      // Nothing has reached the server. Take the message back the way a
      // refused send is taken back, so the composer restores it.
      setDeferredSend(null);
      setMessages((prev) => prev.filter((m) => m !== deferredSend.message));
      setRejectedMessage(deferredSend.message);
      return;
    }
    // Signal the backend to stop generating via the pausedStreaming flag
    if (userId && conversationIdRef.current) {
      void graphql(
        {
          query: updateChatbotConversation,
          variables: {
            input: {
              userId,
              conversationId: conversationIdRef.current,
              pausedStreaming: true,
            },
          },
          authMode: "userPool",
        },
        { errorMode: "silent" },
      );
    }

    if (remoteActive) {
      reconnectProgress();
      return;
    }
    abortControllerRef.current?.abort();
    // Always leave a record of the stopped run. The Stop button is shown during
    // every streaming phase, so a run can be stopped before any text token
    // arrives (mid tool-call/reasoning) — finalize an assistant entry with
    // whatever partial content + tool steps exist plus a "stopped" marker,
    // instead of snapping to idle with no trace.
    const partialContent = streamingContentRef.current;
    const partialSteps = streamingToolStepsRef.current.map((s) =>
      s.status === "running" ? { ...s, status: "done" as const } : s,
    );
    // Read before setMessages — see the `done` handler in startStream.
    const partialBlocks = dataBlocksRef.current;
    setMessages((prev) => [
      ...prev,
      {
        conversationId: conversationIdRef.current,
        role: "assistant",
        renderKey: streamingMessageKey,
        content: partialContent,
        timestamp: new Date(),
        toolSteps: partialSteps.length > 0 ? partialSteps : undefined,
        stopped: true,
        dataBlocks: partialBlocks,
      },
    ]);
    setStreamingContent("");
    streamingContentRef.current = "";
    streamingToolStepsRef.current = [];
    dataBlocksRef.current = new Map();
    setStreamingDataBlocks(dataBlocksRef.current);
    setStreamingToolSteps([]);
    setIsStreaming(false);
    setPhase("idle");
  }, [
    userId,
    remoteActive,
    reconnectProgress,
    streamingMessageKey,
    deferredSend,
  ]);

  // ── Rename conversation ──

  const renameConversation = useCallback(
    (convId: string, newName: string) => {
      if (!userId) return;
      // Optimistic update
      setConversations((prev) =>
        prev.map((c) =>
          c.conversationId === convId ? { ...c, title: newName } : c,
        ),
      );
      void graphql(
        {
          query: updateChatbotConversation,
          variables: {
            input: { userId, conversationId: convId, name: newName },
          },
          authMode: "userPool",
        },
        { errorMode: "silent" },
      );
    },
    [userId],
  );

  // ── Archive conversation ──

  const archiveConversation = useCallback(
    (convId: string) => {
      if (!userId) return;
      // Optimistic removal from sidebar
      setConversations((prev) =>
        prev.filter((c) => c.conversationId !== convId),
      );
      // If archived the active conversation, start a new chat
      if (convId === conversationIdRef.current) {
        const newId = crypto.randomUUID();
        setConversationId(newId);
        setMessages([]);
        setStreamingContent("");
        streamingContentRef.current = "";
        previousResponseIdRef.current = null;
        setPhase("idle");
        setError(null);
        setErrorSource(null);
      }
      void graphql(
        {
          query: updateChatbotConversation,
          variables: {
            input: { userId, conversationId: convId, archived: true },
          },
          authMode: "userPool",
        },
        { errorMode: "silent" },
      );
    },
    [userId],
  );

  const retryLoad = useCallback(() => {
    hasLoadedMessagesRef.current = false;
    void loadOnOpen();
  }, [loadOnOpen]);

  return {
    messages,
    conversationId,
    // A recovered turn may coexist with the last local answer while its
    // saved input is still indexing. Never reuse that answer's row key.
    streamingMessageKey: remoteActive
      ? `progress:${conversationId}:${progress.snapshot?.turn_id ?? "active"}`
      : streamingMessageKey,
    streamingContent:
      replayed && !remoteHasSavedAnswer
        ? spliceDataBlocks(replayed.content, replayed.blocks)
        : streamingContent,
    streamingDataBlocks:
      replayed && !remoteHasSavedAnswer ? replayed.blocks : streamingDataBlocks,
    streamingToolSteps:
      replayed && !remoteHasSavedAnswer ? replayed.steps : streamingToolSteps,
    // The lock/status poll can lag the completed answer. Keep Send blocked
    // until it catches up, but don't show generation for an answer on screen.
    phase: deferredSend
      ? "fetching_context"
      : replayed
        ? remoteHasSavedAnswer
          ? "idle"
          : replayed.phase
        : phase,
    isStreaming: isStreaming || remoteActive || !!deferredSend,
    isCheckingStatus: progress.checking && closingTurn !== conversationId,
    rejectedMessage,
    isLoadingHistory,
    error,
    errorSource,
    warning,
    reasoningEffort,
    setReasoningEffort,
    webSearchEnabled,
    setWebSearchEnabled,
    sendMessage,
    editMessage,
    stopStreaming,
    startNewChat,
    pendingAttachments,
    isUploading,
    addAttachment,
    removeAttachment,
    conversations,
    isLoadingConversations,
    hasMoreConversations,
    loadMoreConversations,
    selectConversation,
    renameConversation,
    archiveConversation,
    retryLoad,
  };
}
